import React from 'react';
import { Navigate, Route, Routes } from 'react-router-dom';

import RegisterForm from '@/components/RegisterForm';
import { loginLegacyRouteManifest } from '@/loginLegacyRouteManifest';
import AccessDeniedPage from '@/pages/AccessDeniedPage';
import AuthContinuePage from '@/pages/AuthContinuePage';
import EmailCallbackPage from '@/pages/EmailCallbackPage';
import LoginPage from '@/pages/LoginPage';
import LoginPageQuiet from '@/pages/LoginPageQuiet';
import SessionExpiredPage from '@/pages/SessionExpiredPage';
import TenantChooserPage from '@/pages/TenantChooserPage';

function LegacyLoginPage({ source }: { source: string }) {
  return (
    <main style={{ fontFamily: 'Inter, sans-serif', padding: '24px' }}>
      <h1>Legacy login/auth page migrated</h1>
      <p>This route is wired during Task 5 bulk migration.</p>
      <code>{source}</code>
    </main>
  );
}

/**
 * Choose Charter (B) vs Quiet Authority (A) based on host.
 *
 *   - login.bassitai.com / login.bassitbrosai.com  → Charter (B)
 *   - tenant.bassitai.com / console.bassitai.com / admin.bassitai.com → Quiet (A)
 *
 * Falls back to Charter for any unknown host so the marketing-adjacent
 * surface stays editorial.
 */
function chooseLoginVariant(): 'charter' | 'quiet' {
  if (typeof window === 'undefined') return 'charter';
  const host = String(window.location.hostname || '').toLowerCase();
  if (host.startsWith('login.')) return 'charter';
  if (host.startsWith('console.') || host.startsWith('admin.')) return 'quiet';
  // Tenant subdomain heuristic: any other 3+ part *.bassitai.* host.
  const parts = host.split('.').filter(Boolean);
  if (parts.length >= 3 && (host.endsWith('bassitai.com') || host.endsWith('bassitbrosai.com'))) {
    return 'quiet';
  }
  return 'charter';
}

function LoginEntry() {
  const variant = chooseLoginVariant();
  return variant === 'quiet' ? <LoginPageQuiet /> : <LoginPage />;
}

export function LoginRoutes() {
  return (
    <Routes>
      <Route path="/" element={<LoginEntry />} />
      <Route path="/login" element={<LoginEntry />} />
      <Route path="/login/charter" element={<LoginPage />} />
      <Route path="/login/quiet" element={<LoginPageQuiet />} />
      <Route path="/register" element={<RegisterForm />} />
      <Route path="/tenant-chooser" element={<TenantChooserPage />} />
      <Route path="/auth/continue" element={<AuthContinuePage />} />
      <Route path="/session-expired" element={<SessionExpiredPage />} />
      <Route path="/access-denied" element={<AccessDeniedPage />} />
      <Route path="/auth/gmail/callback" element={<EmailCallbackPage />} />
      <Route path="/auth/outlook/callback" element={<EmailCallbackPage />} />
      <Route path="/auth/yahoo/callback" element={<EmailCallbackPage />} />
      <Route path="/auth/exchange/callback" element={<EmailCallbackPage />} />
      {loginLegacyRouteManifest.map((route) => (
        <Route
          key={route.path}
          path={route.path}
          element={<LegacyLoginPage source={route.source} />}
        />
      ))}
      <Route path="*" element={<Navigate to="/login" replace />} />
    </Routes>
  );
}
